import {CSSProperties, FC, useEffect, useMemo, useState} from "react";
import {
    Box,
    Card,
    Checkbox,
    Flex,
    Group,
    Text,
    FileButton,
    Button,
    Overlay,
    Tooltip,
    Stack,
    ActionIcon, rgba, ScrollArea, Anchor, Grid, rem,
} from "@mantine/core";
import { Dropzone, FileRejection } from '@mantine/dropzone';
import '@mantine/dropzone/styles.css';
import { NewCommunicationFormProps } from "@/PageComponents/Message/Communication/NewMessageForm";
import * as Enums from '@/utils/enums';
import {useQuery} from "@tanstack/react-query";
import Fetch from "@/utils/Fetch";
import Image from "next/image";
import {
    IconDragDrop2,
    IconFile, IconFileText, IconPaperclip, IconTool, IconUpload, IconX
} from "@tabler/icons-react";
import moment from "moment";
import styles from '@/PageComponents/Message/Communication/MessageAttachments.module.css'
import Constants from "@/utils/constants";
import Helper from "@/utils/helper";
import OptionService from "@/services/option/option-service";
import {showNotification} from "@mantine/notifications";
import {Attachment} from "@/interfaces/api/models";
import {getTablerIconForContentType} from "@/PageComponents/Message/MessageItems/MessageAttachmentItem";

const iconSize = 40
const smallIconSize = 30
const cardSize = 75

interface FormAttachment {
    FormDefinitionID: string;
    Module: number;
    ItemID: string;
    ExpireDate: string | null;
    FormStatus: number;
    AssociatedModule: number;
    AssociatedItemID: string;
    CompletedDate: string;
    Invalid: boolean;
    FormDefinitionDisplayName: string;
    FormDefinitionDescription: string;
    LatestFormDefinitionID: string;
    FormItems: {
        FormHeaderID: string;
        FormDefinitionFieldID: string;
        DisplayOrder: number;
        Section: string | null;
        DataResult: string;
        Description: string;
        Line: number | null;
        SectionGroup: number | null;
        DataType: string;
        Required: boolean;
        SectionID: string;
        SectionHeading: string;
        SectionDescription: string;
        SectionRepeatable: boolean;
        SectionDisplayOrder: number;
        ParentSectionID: string | null;
        ParentSectionGroup: number;
        ID: string;
        IsActive: boolean;
        CreatedBy: string;
        CreatedDate: string;
        ModifiedBy: string;
        ModifiedDate: string;
        RowVersion: string;
    }[];
    FormDefinition: {
        MasterID: string;
        Version: number;
        Name: string;
        Module: number;
        Description: string;
        FormRule: number;
        ExpireTimespan: number;
        StructureLocked: boolean;
        NonExpiring: boolean;
        FormDefinitionStatus: number;
        FormDefinitionFields: any[];
        Sections: any[];
        JobTypes: any[];
        ID: string;
        IsActive: boolean;
        CreatedBy: string;
        CreatedDate: string;
        ModifiedBy: string;
        ModifiedDate: string;
        RowVersion: string;
    };
    ID: string;
    IsActive: boolean;
    CreatedBy: string;
    CreatedDate: string;
    ModifiedBy: string;
    ModifiedDate: string;
    RowVersion: string;
}

export type DocItemType = 'quote' | 'invoice' | 'purchase_order' | 'job_card' | 'workshop' | 'sign_off' | 'job_sheet'

interface LocalFileAttachment {
    AttachmentType: number
    Description: string
    ItemID: string
    FileBase64: string
    FileName: string
    ID: string
    dataUrl: string
}

const readFile = async (itemId: any, file: any, fileSizeUnit = 'mb', fileSizeValue = 2) => {
    return new Promise<LocalFileAttachment | null>((resolve: any) => {
        let reader = new FileReader();
        reader.onloadend = async function () {
            let b64 = (reader.result as string)?.replace(/^data:.+;base64,/, '');
            let uploadLength = 2;

            let dataUrl = ''
            const type = (reader.result as string)?.match(/[^:]\w+\/[\w-+\d.]+(?=;|,)/)?.[0];
            if(type?.startsWith('image/')) {
                const blob = await fetch(`data:${type};base64,${b64}`)
                    .then((res: any) => res.blob());
                // Create a URL from the Blob
                dataUrl = URL.createObjectURL(blob);
            }


            if (fileSizeUnit == 'kb') {
                uploadLength = b64.length / 1024;
            } else if (fileSizeUnit == 'mb') {
                uploadLength = b64.length / 1024 / 1024;
            } else if (fileSizeUnit == 'gb') {
                uploadLength = b64.length / 1024 / 1024 / 1024;
            }

            // base64 converts 6bits to 8bits when encoding, so the actual file size is 3/4
            let scalingFactor = Constants.base64BitScalingFactor;
            uploadLength *= scalingFactor;

            if (uploadLength > +fileSizeValue) {
                resolve(null);
            } else {
                let attachment = {
                    AttachmentType: Enums.AttachmentType.Other,
                    Description: file.name,
                    ItemID: itemId,
                    FileBase64: b64,
                    FileName: file.name,
                    ID: Helper.emptyGuid(),
                    dataUrl: dataUrl
                };
                resolve(attachment);
            }
        };
        reader.readAsDataURL(file);
    });
};

const isDocumentSendingForms = (formRule: any, documentDefinitionMetaData: any, selectedDocuments: DocItemType[]) => {
    if (!documentDefinitionMetaData) {
        return false;
    }

    let jobDocuments = documentDefinitionMetaData.JobDocuments;
    let def:any = null;

    if (selectedDocuments.includes('job_card')) {
        def = jobDocuments.find((x: any) => x.DocumentType === Enums.DocumentType.JobCardCustomer);
        if (def) {
            if ((def.ShowJobForms && formRule === Enums.FormRule.Job) || (def.ShowCustomerForms && formRule === Enums.FormRule.Customer)) {
                return true;
            }
        }
    }
    if (selectedDocuments.includes('sign_off')) {
        def = jobDocuments.find((x: any) => x.DocumentType === Enums.DocumentType.JobCardSignOff);
        if (def) {
            if ((def.ShowJobForms && formRule === Enums.FormRule.Job) || (def.ShowCustomerForms && formRule === Enums.FormRule.Customer)) {
                return true;
            }
        }
    }
    if (selectedDocuments.includes('job_sheet')) {
        def = jobDocuments.find((x: any) => x.DocumentType === Enums.DocumentType.JobCardJobSheet);
        if (def) {
            if ((def.ShowJobForms && formRule === Enums.FormRule.Job) || (def.ShowCustomerForms && formRule === Enums.FormRule.Customer)) {
                return true;
            }
        }
    }

    return false;
}

const getItemAttachments = (itemId: string, module: number, secondaryItemId?: string, secondaryModule?: number) => {
    return Promise.all([
        Fetch.get({
            url: '/Attachment/GetItemAttachmentsByModule',
            params: {
                itemID: itemId,
                module: module,
            }
        }).then((res: any) => {
            if(!res.Results) {
                throw new Error(res.serverMessage || res.message || 'something went wrong')
            }
            return res.Results as Attachment[]
        }).catch(
            (e: any) => {
                throw new Error('Could not fetch item attachments')
            }
        ),
        secondaryItemId && secondaryModule !== undefined ? Fetch.get({
            url: '/Attachment/GetItemAttachmentsByModule',
            params: {
                itemID: secondaryItemId,
                module: secondaryModule,
            }
        }).then((res: any) => {
            if(!res.Results) {
                throw new Error(res.serverMessage || res.message || 'something went wrong')
            }
            return res.Results as Attachment[]
        }).catch(
            (e: any) => {
                throw new Error('Could not fetch item attachments')
            }
        ) :
        [],
    ])
}
const getItemForms = async (itemId: string, itemModule: string) => {
    return Fetch.get({
        url: '/Form/GetByItemID',
        params: {
            itemID: itemId,
            itemModule: itemModule,
        }
    }).then((res: any) => {
        if(!res.Results) {
            throw new Error(res.serverMessage || res.message || 'something went wrong')
        }
        return res.Results
    }).catch(
        (e: any) => {
            throw new Error('Could not fetch item forms')
        }
    )
}

export const FileDetails: FC<{ file: Attachment }> = ({ file }: any) => {
    return (
        <Stack gap={0}>
            <p style={{fontSize: 12, marginTop: 5, marginBottom: 5}}>
                <strong>Filename:</strong> {file.Description || file.FileName}<br/>
                {/*<strong>Description:</strong> {file.Description}<br/>*/}
                {file.FileSize && <><strong>File Size:</strong> {file.DisplayFileSize ?? file.FileSize + ' B'} <br/> </>}
                {file.ContentType && <><strong>Content Type:</strong> {file.ContentType}<br/></>}
                <strong>Attachment Type:</strong> {Enums.getEnumStringValue(Enums.AttachmentType, file.AttachmentType)}<br/>
                {file.CreatedBy && <><strong>Created By:</strong> { file.CreatedBy }<br/> </> }
                {file.CreatedDate && <><strong>Created Date:</strong> {moment(file.CreatedDate).format('DD MMMM, YYYY - hh:mm a')}</> }
                {/*{file.IsSecure && <><br/><strong>Is Secure:</strong> {file.IsSecure ? 'Yes' : 'No'}<br/> </> }*/}
                {/*<br />*/}
            </p>
        </Stack>
    );
};

export const FormDetails: FC<{ form: FormAttachment}> = ({ form }: any) => {
    return (
        <Stack gap={0}>
            <p style={{fontSize: 12, marginTop: 5, marginBottom: 5}}>
                <strong>Name:</strong> {form.FormDefinition.Name}<br/>
                <strong>Description:</strong> {form.FormDefinition.Description}<br/>
                <strong>Status:</strong>&nbsp;
                <span style={{color: form.FormStatus === Enums.FormStatus.Draft ? 'var(mantine-color-yellow-6)' : 'inherit'}}>
                    {Enums.getEnumStringValue(Enums.FormStatus, form.FormStatus)}
                </span><br/>
                <strong>Linked to:</strong> {Enums.getEnumStringValue(Enums.FormRule, form.FormDefinition.FormRule)}<br/>
                <strong>Completed By:</strong> {form.CreatedBy}<br/>
                <strong>Completed Date:</strong> {moment(form.CompletedDate).format('DD MMMM, YYYY - hh:mm a')}<br/>
                {
                    form.ExpireDate &&
                        <>
                            <strong>Expire Date:</strong> {moment(form.ExpireDate).format('DD MMMM, YYYY - hh:mm a')}<br/>
                        </>
                }
                {
                    form.FormDefinition.Sections.length > 0 && <>
                        <strong>Form Sections:</strong>
                        {
                            form.FormDefinition.Sections.map((section: any, index: any) => (
                                <div key={index}>
                                    <strong>Section {index + 1}:</strong> {section}<br/>
                                </div>
                            ))
                        }
                    </>
                }
            </p>
        </Stack>
    );
};


const getFileContentType = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (!extension) {
        return 'unknown';
    }

    switch (extension) {
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'bmp':
            return 'image';

        case 'pdf':
            return 'application/pdf';

        case 'doc':
        case 'docx':
            return 'application/msword';

        case 'xls':
        case 'xlsx':
            return 'application/vnd.ms-excel';

        case 'ppt':
        case 'pptx':
            return 'application/vnd.ms-powerpoint';

        case 'txt':
            return 'text/plain';

        case 'csv':
            return 'text/csv';

        case 'mp4':
        case 'mkv':
        case 'avi':
            return 'video';

        case 'mp3':
        case 'wav':
        case 'flac':
            return 'audio';

        case 'zip':
        case 'rar':
        case '7z':
            return 'application/zip';

        case 'html':
        case 'htm':
            return 'text/html';

        case 'json':
            return 'application/json';

        case 'xml':
            return 'application/xml';

        default:
            return 'unknown';
    }
};

const contentTypeIcon = (contentType: string) => {
    const Element = getTablerIconForContentType(contentType)
    return <Element size={iconSize}
                    style={{
                        zIndex: 0,
                        position: 'absolute',
                        left: `calc(50% - ${iconSize/2}px)`,
                        top: `calc(50% - ${iconSize/2}px)`,
                        color: 'initial'
                    }}
                    stroke={2}
    />
}

export const getIconForDoc = (dt: DocItemType) => {
    const size= iconSize
    const style: CSSProperties = {zIndex: 0, position: 'absolute', left: `calc(50% - ${size/2}px)`, top: `calc(50% + ${size/12}px - ${size/2}px)`}
    switch (dt) {
        case 'quote':
            return <img src={'/sc-icons/quotes-blue.svg'} alt={''} style={{...style}} width={size} height={size}/>
        case 'invoice':
            return <img src={'/sc-icons/invoices-blue.svg'} alt={''} style={{...style}} width={size} height={size}/>
        case "job_card":
        case 'job_sheet':
        case 'sign_off':
            return <img src={'/sc-icons/jobs-blue.svg'} alt={''} style={{...style}} width={size} height={size}/>
        // legacy icons no longer used - all job cards now have the same icon
        /*case 'job_sheet':
            return <IconIdBadge2
                size={size}
                style={style}
                stroke={2}
            />
        case 'sign_off':
            return <IconSignature
                size={size}
                style={style}
                stroke={2}
            />*/
        case 'purchase_order':
            return <img src={'/sc-icons/purchases-blue.svg'} alt={''} style={{...style}} width={size} height={size}/>
        case 'workshop':
            return <IconTool
                size={size}
                style={style}
                stroke={2}
            />
        default:
            return <IconFile
                size={size}
                style={style}
                stroke={2}
            />

    }
}

const MessageAttachments: FC<
    NewCommunicationFormProps & {
    defaultDocs: DocItemType[]
    onDocumentsChanged: (docs: DocItemType[]) => void;
    onFormsChanged: (forms: FormAttachment[]) => void;
    onAttachmentsChanged: (attachments: (LocalFileAttachment| Attachment)[]) => void;
    onLocalAttachmentsChanged: (attachments: (LocalFileAttachment| Attachment)[]) => void;
    selectedDocs?: DocItemType[];
    selectedForms?: string[];
    selectedAttachments?: string[];
    selectedLocalFiles?: LocalFileAttachment[];
}
> = ({
    defaultDocs,
    selectedDocs: propsSelectedDocs,
    selectedForms: propsSelectedForms,
    selectedAttachments: propsSelectedAttachments,
    selectedLocalFiles: propsSelectedLocalFiles,
    ...props
}: any) => {

    const {data: moduleAttachments} = useQuery(
        ['moduleAttachments', props.item, props.moduleCode, props.customer?.ID, props.supplier?.ID],
        () => getItemAttachments(
            props.item.ID,
            +props.moduleCode,
            props.customer?.ID || props.supplier?.ID,
            props.customer ? Enums.Module.Customer : props.supplier ? Enums.Module.Supplier : undefined
        ))

    const {data: formAttachments} = useQuery<FormAttachment[]>(
        ['formAttachments', props.item],
        () => getItemForms(props.item.ID, props.item.Module),
        {
            enabled: +props.moduleCode === Enums.Module.JobCard || +props.moduleCode === Enums.Module.Customer
        })

    // Used memoization for selected attachment and forms
    const [selectedAttachments, setSelectedAttachments] = useState<string[]>([]);
    const [selectedLocalFiles, setSelectedLocalFiles] = useState<LocalFileAttachment[]>([]);
    const [selectedForms, setSelectedForms] = useState<string[]>([]);
    const [selectedDocs, setSelectedDocs] = useState<DocItemType[]>(defaultDocs ?? []);

    useEffect(() => {
        if (propsSelectedDocs !== undefined) setSelectedDocs(propsSelectedDocs);
    }, [propsSelectedDocs]);

    useEffect(() => {
        if (propsSelectedForms !== undefined) setSelectedForms(propsSelectedForms);
    }, [propsSelectedForms]);

    useEffect(() => {
        if (propsSelectedAttachments !== undefined) setSelectedAttachments(propsSelectedAttachments);
    }, [propsSelectedAttachments]);

    useEffect(() => {
        if (propsSelectedLocalFiles !== undefined) setSelectedLocalFiles(propsSelectedLocalFiles);
    }, [propsSelectedLocalFiles]);

    /*useEffect(() => {
        console.log(
            'docs', selectedDocs,
            'forms', selectedForms,
            'attachments', selectedAttachments,
            'local files', selectedLocalFiles
        )

    }, [selectedAttachments, selectedForms, selectedDocs, selectedLocalFiles]);*/

    const handleAttachmentChange = (attachmentIds: string[]) => {
        setSelectedAttachments(attachmentIds);

        props.onAttachmentsChanged(
            attachmentIds.map((id: any) => moduleAttachments && [...(moduleAttachments[0]), ...(moduleAttachments[1])]?.find((x: any) => x.ID === id)) as Attachment[]
        )
    }

    const removeAttachment = (x: string) => {
        setSelectedAttachments((p: any) => {
            const newAttachments = p.filter((y: any) => x !== y)
            props.onAttachmentsChanged(
                newAttachments.map((id: any) => moduleAttachments && [...(moduleAttachments[0]), ...(moduleAttachments[1])]?.find((x: any) => x.ID === id)) as Attachment[]
            )
            return newAttachments
        })
    }

    const handleFormChange = (formIds: string[]) => {
        setSelectedForms(formIds);
        props.onFormsChanged(
            formIds.map((id: any) => formAttachments?.find((x: any) => x.ID === id)) as FormAttachment[]
        )
    }

    const removeForm = (x: string) => {
        setSelectedForms((p: any) => {
            const newForms = p.filter((y: any) => x !== y)
            props.onFormsChanged(
                newForms.map((id: any) => formAttachments?.find((x: any) => x.ID === id)) as FormAttachment[]
            )
            return newForms
        })
    }

    const handleDocChange = (docTypes: DocItemType[]) => {
        setSelectedDocs(docTypes);
        props.onDocumentsChanged(docTypes);
    }

    const removeDoc = (x: DocItemType) => {
        setSelectedDocs((p: any) => {
            const newdocs = p.filter((y: any) => x !== y)
            props.onDocumentsChanged(newdocs)
            return newdocs
        })
    }

    const {data: fileSizeSetting} = useQuery(
        ['fileSettings'],
        () => OptionService.getOption('System Settings', 'File Upload Size')
    )

    const maxFileSizeBytes = useMemo(() => {
        const value = +(fileSizeSetting as any)?.OptionValue || 2;
        const unit = ((fileSizeSetting as any)?.Unit || 'mb').toLowerCase();
        if (unit === 'kb') return value * 1024;
        if (unit === 'mb') return value * 1024 ** 2;
        if (unit === 'gb') return value * 1024 ** 3;
        return value * 1024 ** 2;
    }, [fileSizeSetting]);

    const handleReject = (rejections: FileRejection[]) => {
        rejections.forEach((rejection) => {
            const isTooLarge = rejection.errors.some(e => e.code === 'file-too-large');
            if (isTooLarge) {
                showNotification({
                    id: 'fileTooLarge',
                    message: `All attachment(s) must be smaller than ${(fileSizeSetting as any)?.OptionValue ?? 2}${((fileSizeSetting as any)?.Unit ?? 'mb').toUpperCase()}`,
                    color: 'yellow.7',
                    autoClose: 3000
                })
            } else {
                showNotification({
                    id: 'fileRejected',
                    title: 'File rejected',
                    message: `${rejection.file.name}: ${rejection.errors.map(e => e.message).join(', ')}`,
                    color: 'red.7',
                    autoClose: 3000
                })
            }
        });
    }

    const handleFileChange = async (files: File[]) => {
        const newFiles: LocalFileAttachment[] = []

        for (const file of files) {
            const attachment = await readFile(props.itemId, file, (fileSizeSetting as any)?.Unit, (fileSizeSetting as any)?.OptionValue);
            if (attachment) {
                if (selectedLocalFiles.some((x: any) => x.FileName === attachment.FileName)) {
                    showNotification({
                        id: 'fileTooLarge',
                        title: 'File already added ',
                        message: attachment.FileName,
                        color: 'yellow.7',
                        autoClose: 3000
                    })
                } else {
                    newFiles.push(attachment)
                }
            } else {
                showNotification({
                    id: 'fileTooLarge',
                    message: `All attachment(s) must be smaller than ${(fileSizeSetting as any)?.OptionValue ?? 2}${((fileSizeSetting as any)?.Unit ?? 'mb').toUpperCase()}`,
                    color: 'yellow.7',
                    autoClose: 3000
                })
            }
        }
        setSelectedLocalFiles((p: any) => {
            const newItems = [...p, ...newFiles]
            props.onLocalAttachmentsChanged(newItems)
            return newItems
        })
    }

    const removeLocalFile = (x: LocalFileAttachment) => {
        setSelectedLocalFiles((p: any) => {
            const newItems = p.filter((y: any) => x.FileName !== y.FileName)
            props.onLocalAttachmentsChanged(newItems)
            return newItems
        })
    }

    const handleClearAll = () => {
        setSelectedDocs([]);
        setSelectedForms([]);
        setSelectedAttachments([]);
        setSelectedLocalFiles([]);
        props.onDocumentsChanged([]);
        props.onFormsChanged([]);
        props.onAttachmentsChanged([]);
        props.onLocalAttachmentsChanged([]);
    }

    const allAttachmentsList = useMemo(() => {
        if (!moduleAttachments) return [];
        return [...(moduleAttachments[0]), ...(moduleAttachments[1])]
            ?.reduce((p: any, c: any) => (p.some((x: any) => x.ID === c.ID) ? p : [...p, c]), [] as Attachment[]);
    }, [moduleAttachments]);

    const groupedFormAttachments = useMemo(() => {
        if (!formAttachments) return [];
        const groups: { masterID: string; headers: FormAttachment[] }[] = [];
        const indexByMasterID = new Map<string, number>();

        formAttachments.forEach((fh: FormAttachment) => {
            const masterID = fh.FormDefinition?.MasterID ?? fh.FormDefinitionID;
            if (indexByMasterID.has(masterID)) {
                groups[indexByMasterID.get(masterID)!].headers.push(fh);
            } else {
                indexByMasterID.set(masterID, groups.length);
                groups.push({ masterID, headers: [fh] });
            }
        });

        groups.forEach(group => {
            if (group.headers.length > 1) {
                group.headers.sort((a, b) => {
                    const aDate = a.CreatedDate ? new Date(a.CreatedDate).getTime() : 0;
                    const bDate = b.CreatedDate ? new Date(b.CreatedDate).getTime() : 0;
                    return aDate - bDate;
                });
            }
        });

        return groups;
    }, [formAttachments]);

    const handleSelectAllAttachments = () => {
        const allIds = allAttachmentsList.map((x: any) => x.ID);
        handleAttachmentChange(allIds);
    }

    const documentLabelMapping = useMemo(() => {
        return {
            'quote': 'Quote',
            'invoice': 'Invoice',
            'purchase_order': 'Purchase Order',
            'job_card': props.documentDefinitionMetaData?.JobDocuments.find((x: any) => x.DocumentType === Enums.DocumentType.JobCardCustomer)?.IsActive && props.documentDefinitionMetaData?.JobDocuments.find((x: any) => x.DocumentType === Enums.DocumentType.JobCardCustomer)?.Title || 'Job Card',
            'workshop': 'Workshop',
            'sign_off': props.documentDefinitionMetaData?.JobDocuments.find((x: any) => x.DocumentType === Enums.DocumentType.JobCardSignOff)?.IsActive && props.documentDefinitionMetaData?.JobDocuments.find((x: any) => x.DocumentType === Enums.DocumentType.JobCardSignOff)?.Title || 'Sign Off',
            'job_sheet': props.documentDefinitionMetaData?.JobDocuments.find((x: any) => x.DocumentType === Enums.DocumentType.JobCardJobSheet)?.IsActive && props.documentDefinitionMetaData?.JobDocuments.find((x: any) => x.DocumentType === Enums.DocumentType.JobCardJobSheet)?.Title || 'Job Sheet',
        }
    }, [props.documentDefinitionMetaData?.JobDocuments])

    const totalSelected = selectedDocs.length + selectedForms.length + selectedAttachments.length + selectedLocalFiles.length;

    const hasItemsToAttach = useMemo(() => {
        const hasJobCard = (+props.moduleCode === Enums.Module.JobCard || (props.item && +props.item.Module === Enums.Module.JobCard));
        const hasQuote = +props.moduleCode === Enums.Module.Quote;
        const hasForms = formAttachments && formAttachments.length !== 0;
        const hasInvoice = +props.moduleCode === Enums.Module.Invoice;
        const hasPO = +props.moduleCode === Enums.Module.PurchaseOrder;
        const hasAttachments = allAttachmentsList.length > 0;

        return hasJobCard || hasQuote || hasForms || hasInvoice || hasPO || hasAttachments;
    }, [props.moduleCode, props.item, formAttachments, allAttachmentsList]);

    const renderDocSelect = (value: DocItemType, label: string) => (
        <Checkbox.Card
            key={value}
            value={value}
            radius="md"
            mb={8}
            style={{
                borderColor: selectedDocs.includes(value) ? 'var(--mantine-color-scBlue-5)' : undefined,
                backgroundColor: selectedDocs.includes(value) ? 'var(--mantine-color-scBlue-0)' : undefined
            }}
        >
            <Flex justify="space-between" align="center" p="sm" style={{ minWidth: 0 }}>
                <Text size="sm" fw={700} c="scBlue.7" lineClamp={1} style={{ flex: 1 }}>{label}</Text>
                <Checkbox.Indicator style={{ flexShrink: 0 }} />
            </Flex>
        </Checkbox.Card>
    );

    const renderFormSelect = (form: FormAttachment) => (
        <Checkbox.Card
            key={form.ID}
            value={form.ID}
            radius="md"
            mb={8}
            style={{
                borderColor: selectedForms.includes(form.ID) ? 'var(--mantine-color-scBlue-5)' : undefined,
                backgroundColor: selectedForms.includes(form.ID) ? 'var(--mantine-color-scBlue-0)' : undefined
            }}
        >
            <Flex justify="space-between" align="center" p="sm" style={{ minWidth: 0 }}>
                <Flex gap={'xs'} wrap={'nowrap'} align={'center'} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={700} c="scBlue.7" lineClamp={3} style={{ flexShrink: 1 }}>{form.FormDefinition.Name}</Text>
                    {form.FormStatus !== Enums.FormStatus.None &&
                        <Text size="xs" c="gray.7" lineClamp={3} style={{ flexShrink: 0 }} mr={5}>{Enums.getEnumStringValue(Enums.FormStatus, form.FormStatus)}</Text>
                    }
                </Flex>
                <Checkbox.Indicator style={{ flexShrink: 0 }} />
            </Flex>
        </Checkbox.Card>
    );

    const renderCopyFormSelect = (form: FormAttachment) => (
        <Checkbox.Card
            key={form.ID}
            value={form.ID}
            radius="md"
            mb={8}
            style={{
                borderColor: selectedForms.includes(form.ID) ? 'var(--mantine-color-scBlue-5)' : undefined,
                backgroundColor: selectedForms.includes(form.ID) ? 'var(--mantine-color-scBlue-0)' : undefined
            }}
        >
            <Flex justify="space-between" align="center" p="sm" style={{ minWidth: 0 }}>
                <Flex gap={'xs'} wrap={'nowrap'} align={'center'} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={700} c="scBlue.7" lineClamp={3} style={{ flexShrink: 1 }}>
                        {form.FormDefinitionDisplayName || form.FormDefinition.Name}
                    </Text>
                    {form.FormStatus !== Enums.FormStatus.None &&
                        <Text size="xs" c="gray.7" lineClamp={3} style={{ flexShrink: 0 }} mr={5}>{Enums.getEnumStringValue(Enums.FormStatus, form.FormStatus)}</Text>
                    }
                </Flex>
                <Checkbox.Indicator style={{ flexShrink: 0 }} />
            </Flex>
        </Checkbox.Card>
    );

    const documents = useMemo(() => {

        return (
            selectedDocs.map((x: any) => (
                <Box
                    key={x}
                    mt={2}
                >
                    <Card withBorder
                          radius="md"
                          w={cardSize}
                          h={cardSize}
                          p={0}
                          bg={'scBlue'}
                          c={'scBlue.1'}
                          className={styles.attachmentCheck}
                    >
                        {
                            <Box
                                pos={'relative'}
                                w={'100%'}
                                h={'100%'}
                            >
                                <Flex align={'stretch'}
                                      justify={'center'}
                                      direction={'column'}
                                >
                                    <Text size={'8px'} mt={8} ms={8} me={'xl'} c={'scBlue.0'} lineClamp={2}
                                          maw={cardSize}
                                    >
                                        {documentLabelMapping[x]}
                                    </Text>
                                </Flex>
                                <Tooltip
                                    label={'Remove'}
                                    color={'scBlue.3'}
                                    openDelay={800}
                                >
                                    <ActionIcon
                                        ml={'auto'}
                                        color={'scBlue.2'}
                                        variant={'filled'}
                                        size={'xs'}
                                        c={'white'}
                                        pos={'absolute'}
                                        right={3}
                                        top={3}
                                        style={{zIndex: 5}}
                                        onClick={() => removeDoc(x)}
                                    >
                                        <IconX/>
                                    </ActionIcon>
                                </Tooltip>
                                {
                                    getIconForDoc(x)
                                }
                            </Box>
                        }
                    </Card>
                </Box>
            ))
        )
    }, [selectedDocs])

    const forms = useMemo(() => (
        selectedForms.map((x: any) => {
            const size = smallIconSize
            const formAttachment = formAttachments?.find((y: any) => y.ID === x)
            return (formAttachment &&
                <Tooltip
                    key={x}
                    label={FormDetails({form: formAttachment})}
                    multiline
                    color={'scBlue.9'}
                    openDelay={1200}
                >
                    <Box
                        mt={2}
                    >
                        <Card withBorder
                              radius="md"
                              w={cardSize}
                              h={cardSize}
                              p={0}
                            // bg={'scBlue.0'}
                            // c={'scBlue.7'}
                              bg={'scBlue'}
                              c={'scBlue.1'}
                              className={styles.attachmentCheck}
                        >
                            {
                                <Box
                                    pos={'relative'}
                                    w={'100%'}
                                    h={'100%'}
                                >
                                    <Flex align={'stretch'}
                                          justify={'center'}
                                          direction={'column'}
                                    >
                                        <Text size={'9px'} mt={8} ms={8} me={19} c={'scBlue.1'} lineClamp={3}
                                              maw={cardSize} style={{zIndex: 3}}
                                        >
                                            {formAttachment.FormDefinition.Name}
                                        </Text>
                                        {
                                            formAttachment.FormStatus === Enums.FormStatus.Draft &&
                                            <Text size={'8px'} mt={3} ms={8} me={19} c={'yellow.6'} fw={'bolder'}
                                                  lineClamp={3} maw={cardSize} style={{zIndex: 3}}>
                                                Draft
                                            </Text>
                                        }
                                        {
                                            isDocumentSendingForms(formAttachment, props.documentDefinitionMetaData, selectedDocs) &&
                                            <Text size={'8px'} fw={'bolder'} mt={3} ms={8} me={19} c={'yellow.6'}
                                                  lineClamp={3} style={{zIndex: 3}}
                                                  maw={cardSize}>
                                                In document
                                            </Text>
                                        }

                                    </Flex>
                                    <Tooltip
                                        label={'Remove'}
                                        color={'scBlue.3'}
                                        openDelay={800}
                                    >
                                        <ActionIcon
                                            ml={'auto'}
                                            color={'scBlue.2'}
                                            variant={'filled'}
                                            size={'xs'}
                                            c={'white'}
                                            pos={'absolute'}
                                            right={3}
                                            top={3}
                                            style={{zIndex: 5}}
                                            onClick={() => removeForm(x)}
                                        >
                                            <IconX/>
                                        </ActionIcon>
                                    </Tooltip>
                                    <Flex>
                                        {
                                            <IconFileText
                                                size={size}
                                                style={{
                                                    zIndex: 0,
                                                    marginInline: 'auto',
                                                    marginTop: 5
                                                    // position: 'absolute',
                                                    // left: `calc(50% - ${size / 2}px)`,
                                                    // top: `calc(50% )`
                                                }}
                                                stroke={2}
                                            />
                                        }
                                    </Flex>
                                </Box>
                            }
                        </Card>
                    </Box>
                </Tooltip>
            )
        })
    ), [selectedForms, selectedDocs])

    const attachments = useMemo(() => selectedAttachments.map(
            (x: any) => {
                const att = moduleAttachments?.flatMap((x: any) => x)?.find((y: any) => y.ID === x)
                return att && <Tooltip
                    multiline
                    key={x}
                    color={'scBlue.9'}
                    openDelay={1200}
                    label={FileDetails({file: att})}
                >
                    <Box
                        mt={2}
                    >
                        <Card withBorder radius="md" w={cardSize} h={cardSize}
                              p={0}
                              className={styles.attachmentCheck}
                        >
                            {
                                <Box
                                    pos={'relative'}
                                    w={'100%'}
                                    h={'100%'}
                                >
                                    <Tooltip
                                        label={'Remove'}
                                        color={'scBlue.3'}
                                        openDelay={800}
                                    >
                                        <ActionIcon
                                            ml={'auto'}
                                            color={'scBlue.0'}
                                            variant={'filled'}
                                            size={'xs'}
                                            c={'scBlue'}
                                            pos={'absolute'}
                                            right={3}
                                            top={3}
                                            style={{zIndex: 5}}
                                            onClick={() => removeAttachment(x)}
                                        >
                                            <IconX/>
                                        </ActionIcon>
                                    </Tooltip>
                                    {
                                        att?.ContentType?.startsWith('image/') ?
                                            <Image
                                                fill
                                                objectFit={'center'}
                                                objectPosition={'center'}
                                                src={att.UrlThumb || ''}
                                                alt={''}
                                                style={{
                                                    objectPosition: 'center',
                                                    objectFit: 'contain',
                                                    zIndex: 0
                                                }}
                                            /> :
                                            contentTypeIcon(att.ContentType || '')
                                    }


                                    <Flex align={'stretch'}
                                          justify={'center'}
                                          direction={'column'}
                                          pos={'absolute'}
                                          bottom={0}
                                          c={'#fff'}
                                          pt={2}
                                          w={'100%'}
                                          style={{
                                              backgroundColor: rgba('var(--mantine-color-dark-1)', .4),
                                              color: 'white',
                                              textShadow: `-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000`
                                          }}
                                    >
                                        <Text size={'9px'} ta={'right'} mr={1}>
                                            {moment(att.ModifiedDate).format('DD MMM, YY hh:mm')}
                                        </Text>
                                        <Text size={'xs'} lineClamp={1} maw={cardSize} ta={'right'}>
                                            {att.Description}
                                        </Text>
                                    </Flex>

                                </Box>
                            }
                        </Card>
                    </Box>
                </Tooltip>;
            }
        )
        , [selectedAttachments]);

    const files = useMemo(() => selectedLocalFiles.map(
            (x: any) => {
                return <Tooltip
                    multiline
                    key={x.FileName}
                    color={'scBlue.9'}
                    openDelay={1200}
                    label={FileDetails({file: x as any})}
                >
                    <Box
                        mt={2}
                    >
                        <Card withBorder radius="md" w={cardSize} h={cardSize}
                              p={0}
                              className={styles.attachmentCheck}
                        >
                            {
                                <Box
                                    pos={'relative'}
                                    w={'100%'}
                                    h={'100%'}
                                >
                                    <Tooltip
                                        label={'Remove'}
                                        color={'scBlue.3'}
                                        openDelay={800}
                                    >
                                        <ActionIcon
                                            ml={'auto'}
                                            color={'scBlue.0'}
                                            variant={'filled'}
                                            size={'xs'}
                                            c={'scBlue'}
                                            pos={'absolute'}
                                            right={3}
                                            top={3}
                                            style={{zIndex: 5}}
                                            onClick={() => removeLocalFile(x)}
                                        >
                                            <IconX/>
                                        </ActionIcon>
                                    </Tooltip>
                                    {
                                        !!x.dataUrl ?
                                            <Image
                                                fill
                                                objectFit={'center'}
                                                objectPosition={'center'}
                                                src={x.dataUrl}
                                                alt={''}
                                                style={{
                                                    objectPosition: 'center',
                                                    objectFit: 'contain',
                                                    zIndex: 0
                                                }}
                                            /> : contentTypeIcon(getFileContentType(x.FileName))
                                    }


                                    <Flex align={'stretch'}
                                          justify={'center'}
                                          direction={'column'}
                                          pos={'absolute'}
                                          bottom={0}
                                          c={'#fff'}
                                          pt={2}
                                          w={'100%'}
                                          style={{
                                              backgroundColor: rgba('var(--mantine-color-dark-1)', .4),
                                              color: 'white',
                                              textShadow: `-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000`
                                          }}
                                    >
                                        <Text size={'xs'} lineClamp={2} maw={cardSize} ta={'right'}>
                                            {x.FileName}
                                        </Text>
                                    </Flex>

                                </Box>
                            }
                        </Card>
                    </Box>
                </Tooltip>
            }
        ),
        [selectedLocalFiles]
    );
    return (
        <>
            <Dropzone.FullScreen
                // accept={IMAGE_MIME_TYPE}
                onDrop={handleFileChange}
                onReject={handleReject}
                multiple
                maxSize={maxFileSizeBytes}
            >
                <Group justify="center" gap="xl" mih={220} style={{pointerEvents: 'none'}}>
                    <Dropzone.Accept>
                        <IconUpload
                            style={{width: rem(52), height: rem(52), color: 'var(--mantine-color-scBlue-6)'}}
                            stroke={1.5}
                        />
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                        <IconX
                            style={{width: rem(52), height: rem(52), color: 'var(--mantine-color-yellow-7)'}}
                            stroke={1.5}
                        />
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                        <IconDragDrop2
                            style={{width: rem(52), height: rem(52), color: 'var(--mantine-color-dimmed)'}}
                            stroke={1.5}
                        />
                    </Dropzone.Idle>

                    <div>
                        <Text size="xl" inline ta={'center'}>
                            Drag files here
                        </Text>
                        <Text size="sm" c="dimmed" inline mt={7} ta="center">
                            Files will be added to the email attachments. A file should not
                            exceed {(fileSizeSetting as any)?.OptionValue ?? 2}{((fileSizeSetting as any)?.Unit ?? 'mb').toUpperCase()}
                        </Text>
                    </div>
                </Group>
            </Dropzone.FullScreen>

            <Grid gutter={40}>
                <Grid.Col span={{base: 12, lg: 6}}>
                    <Text fw={700} size="14px" my="xs">Related documents</Text>

                    {!hasItemsToAttach && (
                        <Stack align="center" gap={'sm'} py={40}>
                            <IconPaperclip size={24} />
                            <Text fw={700} c="gray.7">Nothing to attach</Text>
                            <Text size="sm" c="gray.6">There no linked items yet</Text>
                        </Stack>
                    )}

                    <Checkbox.Group
                        value={selectedDocs}
                        onChange={((x: any) => handleDocChange(x as DocItemType[]))}
                    >
                        <Flex wrap="wrap" columnGap={40} rowGap="md">
                            {(+props.moduleCode === Enums.Module.JobCard || (props.item && +props.item.Module === Enums.Module.JobCard)) && (
                                <Box w={{base: '100%', sm: 'calc(50% - 20px)'}}>
                                    <Text size="sm" fw={700} c="gray.7" tt="uppercase" mb="xs">Job cards</Text>
                                    {renderDocSelect('job_card', documentLabelMapping['job_card'])}
                                    {!props.documentDefinitionMetaData && renderDocSelect('workshop', documentLabelMapping['workshop'])}
                                    {((!props.documentDefinitionMetaData || props.documentDefinitionMetaData.JobDocuments.find((x: any) => x.DocumentType === Enums.DocumentType.JobCardSignOff)?.IsActive)) &&
                                        renderDocSelect('sign_off', documentLabelMapping['sign_off'])}
                                    {((!props.documentDefinitionMetaData || props.documentDefinitionMetaData.JobDocuments.find((x: any) => x.DocumentType === Enums.DocumentType.JobCardJobSheet)?.IsActive)) &&
                                        renderDocSelect('job_sheet', documentLabelMapping['job_sheet'])}
                                </Box>
                            )}

                            {+props.moduleCode === Enums.Module.Quote && (
                                <Box w={{base: '100%', sm: 'calc(50% - 20px)'}}>
                                    <Text size="sm" fw={700} c="gray.7" tt="uppercase" mb="xs">Quotes</Text>
                                    {renderDocSelect('quote', documentLabelMapping['quote'])}
                                </Box>
                            )}

                            {formAttachments && formAttachments.length !== 0 && (
                                <Box w={{base: '100%', sm: 'calc(50% - 20px)'}}>
                                    <Text size="sm" fw={700} c="gray.7" tt="uppercase" mb="xs">Forms</Text>
                                    <Checkbox.Group
                                        value={selectedForms}
                                        onChange={handleFormChange}
                                    >
                                        {groupedFormAttachments.flatMap(({ masterID, headers }) => {
                                            if (headers.length === 1) {
                                                return [renderFormSelect(headers[0])];
                                            }
                                            const parent = headers[0];
                                            const copies = headers.slice(1);
                                            return [
                                                renderFormSelect(parent),
                                                <Box key={`${masterID}-copies`} className={styles.copySection}>
                                                    {copies.map(renderCopyFormSelect)}
                                                </Box>
                                            ];
                                        })}
                                    </Checkbox.Group>
                                </Box>
                            )}

                            {+props.moduleCode === Enums.Module.Invoice && (
                                <Box w={{base: '100%', sm: 'calc(50% - 20px)'}}>
                                    <Text size="sm" fw={700} c="gray.7" tt="uppercase" mb="xs">Invoices</Text>
                                    {renderDocSelect('invoice', documentLabelMapping['invoice'])}
                                </Box>
                            )}

                            {+props.moduleCode === Enums.Module.PurchaseOrder && (
                                <Box w={{ base: '100%', sm: 'calc(50% - 20px)' }}>
                                    <Text size="sm" fw={700} c="gray.7" tt="uppercase" mb="xs">Purchase Orders</Text>
                                    {renderDocSelect('purchase_order', documentLabelMapping['purchase_order'])}
                                </Box>
                            )}
                        </Flex>
                    </Checkbox.Group>

                    {allAttachmentsList.length > 0 && (
                        <Box mt="md">
                            <Flex gap={'xl'} align="center" mb="xs">
                                <Text size="sm" fw={700} c="gray.7" tt="uppercase">Attachments ({allAttachmentsList.length})</Text>
                                {
                                    allAttachmentsList.length !== selectedAttachments.length &&
                                    <Anchor size="sm" fw={700} onClick={handleSelectAllAttachments}>Select all</Anchor>
                                }
                            </Flex>
                            <ScrollArea.Autosize mah={500}>
                                <Checkbox.Group
                                    value={selectedAttachments}
                                    onChange={handleAttachmentChange}
                                >
                                    <Flex gap={8} wrap={'wrap'}>
                                        {allAttachmentsList.map((x: any) => (
                                            <Tooltip
                                                multiline
                                                key={x.ID}
                                                color={'scBlue.9'}
                                                openDelay={1200}
                                                label={FileDetails({file: x})}
                                            >
                                                <Box mt={2}>
                                                    <Checkbox.Card radius="md" value={x.ID} w={cardSize} h={cardSize}
                                                        styles={{
                                                            card: {
                                                                borderColor: selectedAttachments.includes(x.ID!) ? 'var(--mantine-color-scBlue-6)' : ''
                                                            }
                                                        }}
                                                        className={styles.attachmentCheck}
                                                    >
                                                        <Box pos={'relative'} w={'100%'} h={'100%'}>
                                                            {selectedAttachments.includes(x.ID!) &&
                                                                <Overlay color="var(--mantine-color-scBlue-5)"
                                                                         zIndex={5} backgroundOpacity={0.2}
                                                                         blur={0}/>
                                                            }
                                                            {x?.ContentType?.startsWith('image/') ?
                                                                <Image
                                                                    fill
                                                                    src={x.UrlThumb || ''}
                                                                    alt={''}
                                                                    style={{ objectPosition: 'center', objectFit: 'contain', zIndex: 0 }}
                                                                /> :
                                                                contentTypeIcon(x.ContentType || '')
                                                            }
                                                            <Checkbox.Indicator
                                                                size={'sm'}
                                                                style={{position: 'absolute', left: 3, top: 3}}
                                                            />
                                                            <Flex align={'stretch'}
                                                                  justify={'center'}
                                                                  direction={'column'}
                                                                  pos={'absolute'}
                                                                  bottom={0}
                                                                  c={'#fff'}
                                                                  pt={2}
                                                                  w={'100%'}
                                                                  style={{
                                                                      backgroundColor: rgba('var(--mantine-color-dark-1)', .4),
                                                                      color: 'white',
                                                                      textShadow: `-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000`
                                                                  }}
                                                            >
                                                                <Text size={'9px'} ta={'right'} mr={1}>
                                                                    {moment(x.ModifiedDate).format('DD MMM, YY hh:mm')}
                                                                </Text>
                                                                <Text size={'xs'} lineClamp={1} maw={cardSize} ta={'right'}>
                                                                    {x.Description}
                                                                </Text>
                                                            </Flex>
                                                        </Box>
                                                    </Checkbox.Card>
                                                </Box>
                                            </Tooltip>
                                        ))}
                                    </Flex>
                                </Checkbox.Group>
                            </ScrollArea.Autosize>
                        </Box>
                    )}
                </Grid.Col>

                <Grid.Col span={{base: 12, lg: 6}}>
                    <Flex gap={'xl'} align="center" my="xs" h={18}>
                        <Text fw={700} size="14px">Selected items ({totalSelected})</Text>
                        {
                            totalSelected > 0 &&
                            <Anchor
                                size="sm"
                                fw={700}
                                onClick={handleClearAll}
                            >
                                Clear all
                            </Anchor>
                        }
                    </Flex>

                    <Box mih={175}>
                        {totalSelected > 0 ? (
                            <>
                                <Flex justify={'start'} align={'start'} gap={8} wrap={'wrap'} mb="xl">
                                    {documents} {forms}
                                </Flex>
                                <Flex justify={'start'} align={'start'} gap={8} wrap={'wrap'}>
                                    {attachments} {files}
                                </Flex>
                            </>
                        ) : (
                            <Flex
                                h={172} align={'center'} justify={'center'}
                            >
                                <Text c="dimmed" ta="center" py="xl">No items selected</Text>
                            </Flex>
                        )}
                    </Box>

                    <Box mt="xl">
                        <FileButton
                            onChange={handleFileChange}
                            accept="*"
                            multiple
                        >
                            {(props) => (
                                <Button {...props} variant="default" fullWidth leftSection={<IconUpload size={16} />}>
                                    Choose file(s)
                                </Button>
                            )}
                        </FileButton>
                    </Box>
                </Grid.Col>
            </Grid>
        </>
    )
};

export default MessageAttachments